import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/** Fallback language whose keys must always be complete. */
const FALLBACK_LANG = 'en';

/**
 * Per-language loaders. Each uses a literal dynamic `import()` so the bundler
 * emits one chunk per language and only the active language is ever fetched —
 * the JSON dictionaries are kept as data files, not inlined into the bundle.
 */
const TRANSLATION_LOADERS: Record<string, () => Promise<unknown>> = {
  en: () => import('./i18n/en.json'),
  ru: () => import('./i18n/ru.json'),
  es: () => import('./i18n/es.json'),
  fr: () => import('./i18n/fr.json'),
  zh: () => import('./i18n/zh.json'),
};

/** Languages the feature ships translations for. */
export const ERROR_REPORTING_LANGUAGES = Object.keys(TRANSLATION_LOADERS);

/**
 * Loads the feature's translation dictionary for a language, or `null` if the
 * language isn't bundled.
 */
export async function loadErrorReportingTranslations(
  lang: string
): Promise<object | null> {
  const loader = TRANSLATION_LOADERS[lang];
  if (!loader) {
    return null;
  }

  // JSON modules expose the data as the default export at runtime; fall back to
  // the module object itself for bundlers that inline the JSON directly.
  const module = (await loader()) as Record<string, unknown>;
  return (module['default'] as object | undefined) ?? module;
}

/**
 * Merges the feature's translations into the app's `TranslateService`, lazily
 * fetching only the active language (and re-fetching when the language changes).
 * Self-contained: the host app needs no `ERROR_REPORTING.*` keys in its own i18n
 * files. No-op when ngx-translate isn't present.
 */
export function provideErrorReportingI18n(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const translate = inject(TranslateService, { optional: true });
    if (!translate) {
      return;
    }

    const load = async (lang?: string): Promise<void> => {
      const target = lang && TRANSLATION_LOADERS[lang] ? lang : FALLBACK_LANG;
      // Re-merge after the host loader finishes; it may replace an earlier dictionary.
      // Dynamic imports already cache each language.
      const dictionary = await loadErrorReportingTranslations(target);
      if (dictionary) {
        translate.setTranslation(target, dictionary, true);
      }
    };

    translate.onLangChange.subscribe(event => void load(event.lang));
    translate.onDefaultLangChange.subscribe(event => void load(event.lang));

    const initial =
      translate.currentLang ?? translate.getDefaultLang() ?? FALLBACK_LANG;

    // Await so the strings are present before first paint, and make sure a
    // language is actually active — otherwise routes that bootstrap before the
    // app sets one (e.g. the standalone /error screen) render raw keys.
    return load(initial).then(() => {
      if (!translate.getDefaultLang()) {
        translate.setDefaultLang(initial);
      }
      if (!translate.currentLang) {
        translate.use(initial);
      }
    });
  });
}
